import { c as _c } from "react/compiler-runtime";
import { withLocale, withLocalePaths } from "@lib/i18n/pagesRouter";
import { SiteShellFooter, SiteShellHeader, type Shell } from "@components/SiteShell";
import Container from "@components/Container";
import DynamicZone, { DynamicZoneComponents } from "@components/DynamicZone";
import MinimalFooter from "@components/MinimalFooter";
import PreviewBanner from "@components/PreviewBanner";
import SEO from "@components/SEO";
import { config } from "@lib/config";
import type { ComponentSectionsCustomerReviews, ComponentSectionsHeroThree, ComponentSectionsHeroV3, ComponentSharedSeo, Page, PageContentSectionsDynamicZone, PageEntity } from "@lib/generated/graphql";
import { getAlternateLanguageHref, getCanonicalLink, getLanguageAlternates, getPagePath, toSiteCanonical } from "@lib/routing";
import { getShellProps } from "@components/SiteShell/getShell";
import getPageData from "@lib/strapi/getPageData";
import getPages from "@lib/strapi/getPages";
import { filterStrapiPages, toSlugSegments, toStaticPagePaths } from "@lib/strapi/utilities";
import getStaticData from "@modules/homepage/sections/CustomerReviews/getStaticData";
import getStaticSectionData from "@modules/homepage/sections/InterestRateChart/getStaticSectionData";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import dynamic from "next/dynamic";
import { Locale } from "next/router";
const ComponentSectionsAccordionV2 = dynamic<any>(() => import("@modules/homepage/sections/AnimatedAccordion"));
const ComponentSectionsBudgetCalculator = dynamic<any>(() => import("@modules/homepage/sections/BudgetCalculator"));
const ComponentSectionsContactUs = dynamic<any>(() => import("@modules/homepage/sections/ContactUs"));
const ComponentSectionsCtaCard = dynamic<any>(() => import("@modules/homepage/sections/CtaCard"));
const ComponentSectionsCtaCardWithRunningLine = dynamic<any>(() => import("@modules/homepage/sections/CtaCardWithRunningLine"));
const ComponentSectionsCtaWithFaqV2 = dynamic<any>(() => import("@modules/homepage/sections/CtaWithAccordion"));
const ComponentSectionsCtaWithFeaturesV2 = dynamic<any>(() => import("@modules/homepage/sections/CtaWithFeatures"));
const ComponentSectionsCtaWithImageV3 = dynamic<any>(() => import("@modules/homepage/sections/CtaWithImageV3"));
const ComponentSectionsCtaWithTestimonials = dynamic<any>(() => import("@modules/homepage/sections/CtaWithTestimonials"));
const ComponentSectionsCtaWithTextV2 = dynamic<any>(() => import("@modules/homepage/sections/CtaWithText"));
const ComponentSectionsCtaWithVideoV2 = dynamic<any>(() => import("@modules/homepage/sections/CtaWithVideo"));
const ComponentSectionsCtaWithVideoV3 = dynamic<any>(() => import("@modules/homepage/sections/CtaWithVideoV3"));
const ComponentSectionsFeaturedIn = dynamic<any>(() => import("@modules/homepage/sections/FeaturedIn"));
const ComponentSectionsFormAndCta = dynamic<any>(() => import("@modules/learning-center/sections/FormAndCta"));
const ComponentSectionsHeadline = dynamic<any>(() => import("@modules/homepage/sections/Headline"));
const ComponentSectionsHeroV2 = dynamic<any>(() => import("@modules/homepage/sections/Hero"));
const ComponentSectionsHeroSecondaryV2 = dynamic<any>(() => import("@modules/homepage/sections/HeroSecondary"));
const ComponentSectionsHeroTwo = dynamic<any>(() => import("@modules/homepage/sections/HeroTwo"));
const ComponentSectionsHeroV3Component = dynamic<any>(() => import("@modules/homepage/sections/HeroV3"));
const ComponentSectionsHeyflow = dynamic<any>(() => import("@modules/homepage/sections/Heyflow"));
const ComponentSectionsCustomerReviews = dynamic<any>(() => import("@modules/homepage/sections/CustomerReviews"));
const ComponentSectionsMediaOutlets = dynamic<any>(() => import("@modules/homepage/sections/MediaOutlets"));
const ComponentSectionsHorizontalRunningLinesV2 = dynamic<any>(() => import("@modules/homepage/sections/HorizontalRunningLines"));
const ComponentSectionsHorizontalScrollCarousel = dynamic<any>(() => import("@modules/homepage/sections/HorizontalScrollCarousel"));
const ComponentSectionsHubspotForm = dynamic<any>(() => import("@modules/homepage/sections/HubspotForm"));
const ComponentSectionsImageCarousel = dynamic<any>(() => import("@modules/homepage/sections/ImageCarousel"));
const ComponentSectionsJobsRunningLine = dynamic<any>(() => import("@modules/homepage/sections/JobsRunningLine"));
const ComponentSectionsOpenJobs = dynamic<any>(() => import("@modules/homepage/sections/OpenJobs"));
const ComponentSectionsRichTextV2 = dynamic<any>(() => import("@modules/homepage/sections/RichTextV2"));
const ComponentSectionsTeamOfExpertsV2 = dynamic<any>(() => import("@modules/homepage/sections/TeamOfExperts"));
const ComponentSectionsTestimonialCards = dynamic<any>(() => import("@modules/homepage/sections/TestimonialCards"));
const ComponentSectionsTestimonialsV2 = dynamic<any>(() => import("@modules/homepage/sections/Testimonials"));
const ComponentSectionsVerticalRunningLinesV2 = dynamic<any>(() => import("@modules/homepage/sections/VerticalRunningLines"));
const ComponentSectionsWorkProcessV2 = dynamic<any>(() => import("@modules/homepage/sections/WorkProcess"));
const ComponentSectionsWorkProcessThreeV2 = dynamic<any>(() => import("@modules/homepage/sections/WorkProcessThree"));
const ComponentSectionsWorkProcessTwoV2 = dynamic<any>(() => import("@modules/homepage/sections/WorkProcessTwo"));
const ComponentSectionsAdvisorMeetings = dynamic<any>(() => import("@modules/homepage/sections/AdvisorMeeting"));
const ComponentSectionsCtaWithFaqV3 = dynamic<any>(() => import("@modules/homepage/sections/CtaWithFaqV3"));
const ComponentSectionsBudgetCheckForm = dynamic<any>(() => import("@modules/homepage/sections/BudgetCheckForm"));
const ComponentSectionsBudgetFormNavigator = dynamic<any>(() => import("@modules/application/components/BudgetFormNavigator"));
const ComponentSectionsInterestRateChart = dynamic<any>(() => import("@modules/homepage/sections/InterestRateChart"));
const ComponentSectionsCurrentRateSummary = dynamic<any>(() => import("@modules/homepage/sections/CurrentRateSummary"));
const dynamicZoneComponents: DynamicZoneComponents<PageContentSectionsDynamicZone> = {
  ComponentSectionsNotaryCostCalculator: dynamic(() => import("@modules/homepage/sections/NotaryCostCalculator").then(mod => mod.NotaryCostCalculatorSection)),
  ComponentSectionsPurchaseCostCalculator: dynamic(() => import("@modules/homepage/sections/PropertyPurchaseCostsCalculator").then(mod => mod.PropertyPurchaseCostsCalculatorSection)),
  ComponentSectionsRepaymentCalculator: dynamic(() => import("@modules/homepage/sections/RepaymentCalculator")),
  ComponentSectionsHeroThree: dynamic<any>(() => import("@modules/homepage/sections/HeroThree")),
  ComponentSectionsWorkProcessFour: dynamic<any>(() => import("@modules/homepage/sections/WorkProcessFour")),
  ComponentSectionsCta: dynamic(() => import("@modules/homepage/sections/Cta")),
  ComponentSectionsBudgetCheckForm,
  ComponentSectionsCtaWithVideoV3,
  ComponentSectionsHeyflow,
  ComponentSectionsCustomerReviews,
  ComponentSectionsMediaOutlets,
  ComponentSectionsFormAndCta,
  ComponentSectionsTestimonialsV2,
  ComponentSectionsCtaWithVideoV2,
  ComponentSectionsAccordionV2,
  ComponentSectionsCtaWithFaqV2,
  ComponentSectionsCtaWithTextV2,
  ComponentSectionsCtaWithFeaturesV2,
  ComponentSectionsHeroV2,
  ComponentSectionsVerticalRunningLinesV2,
  ComponentSectionsHorizontalRunningLinesV2,
  ComponentSectionsHeroSecondaryV2,
  ComponentSectionsCtaCard,
  ComponentSectionsCtaWithTestimonials,
  ComponentSectionsCtaCardWithRunningLine,
  ComponentSectionsTeamOfExpertsV2,
  ComponentSectionsWorkProcessTwoV2,
  ComponentSectionsWorkProcessThreeV2,
  ComponentSectionsHorizontalScrollCarousel,
  ComponentSectionsContactUs,
  ComponentSectionsTestimonialCards,
  ComponentSectionsImageCarousel,
  ComponentSectionsOpenJobs,
  ComponentSectionsJobsRunningLine,
  ComponentSectionsHeroTwo,
  ComponentSectionsWorkProcessV2,
  ComponentSectionsRichTextV2,
  ComponentSectionsCtaWithImageV3,
  ComponentSectionsBudgetCalculator,
  ComponentSectionsHeroV3: ComponentSectionsHeroV3Component,
  ComponentSectionsFeaturedIn,
  ComponentSectionsHeadline,
  ComponentSectionsHubspotForm,
  ComponentSectionsAdvisorMeetings,
  ComponentSectionsCtaWithFaqV3,
  ComponentSectionsBudgetFormNavigator,
  ComponentSectionsInterestRateChart,
  ComponentSectionsCurrentRateSummary,
  ComponentSectionsArticleList: null,
  ComponentSectionsArticleOverviewBanner: null,
  ComponentSectionsArticleSearch: null,
  ComponentSectionsArticleSearchBanner: null,
  ComponentSectionsCategoryCards: null,
  ComponentSectionsPillarArticles: null,
  Error: null
};
const getPageSeoWithCanonicalLink = (page: Page, locale: Locale): ComponentSharedSeo | undefined | null => {
  if (page.seo && page.slug) {
    const pagePath = getPagePath(page)!;
    const canonicalURL = toSiteCanonical(locale, pagePath, page.seo.canonicalURL);
    return {
      ...page.seo,
      canonicalURL
    };
  }
  return page.seo;
};
function CMSPage(t0) {
  const $ = _c(37);
  const {
    page,
    sectionData,
    preview,
    shell,
    isPartner,
    locale,
    alternateLanguageHref
  } = t0;
  let T0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[0] !== alternateLanguageHref || $[1] !== locale || $[2] !== page || $[3] !== preview || $[4] !== sectionData || $[5] !== shell) {
    const sections = page?.contentSections || [];
    const [hero, otherSections] = sections.reduce(_temp, [undefined, []] as [ComponentSectionsHeroV3 | ComponentSectionsHeroThree | undefined, PageContentSectionsDynamicZone[]]);
    let localizedAlternateLanguageHref;
    if ($[13] !== locale || $[14] !== page) {
      const seo = getPageSeoWithCanonicalLink(page, locale);
      localizedAlternateLanguageHref = getAlternateLanguageHref(locale, page.localizations, "page");
      t3 = seo && <SEO seoData={seo} languageAlternates={getLanguageAlternates({
        locale,
        pageUrl: getCanonicalLink(locale, getPagePath(page) ?? "/"),
        canonicalURL: seo.canonicalURL,
        alternateLanguageHref: localizedAlternateLanguageHref
      })} />;
      $[13] = locale;
      $[14] = page;
      $[15] = localizedAlternateLanguageHref;
      $[16] = t3;
    } else {
      localizedAlternateLanguageHref = $[15];
      t3 = $[16];
    }
    if ($[17] !== preview) {
      t4 = preview && <PreviewBanner />;
      $[17] = preview;
      $[18] = t4;
    } else {
      t4 = $[18];
    }
    const t7 = !!page.minimalNavigation;
    const t8 = alternateLanguageHref ?? localizedAlternateLanguageHref;
    if ($[19] !== shell || $[20] !== t7 || $[21] !== t8) {
      t5 = <SiteShellHeader shell={shell} minimal={t7} alternateLanguageHref={t8} />;
      $[19] = shell;
      $[20] = t7;
      $[21] = t8;
      $[22] = t5;
    } else {
      t5 = $[22];
    }
    t6 = hero && <DynamicZone component={hero} index={0} staticComponentData={sectionData} components={dynamicZoneComponents} />;
    T0 = Container;
    t1 = "min-h-[calc(100vh-174px)]";
    t2 = otherSections.map((section_0, index) => <DynamicZone index={hero ? index + 1 : index} component={section_0} key={index} staticComponentData={sectionData} components={dynamicZoneComponents} />);
    $[0] = alternateLanguageHref;
    $[1] = locale;
    $[2] = page;
    $[3] = preview;
    $[4] = sectionData;
    $[5] = shell;
    $[6] = T0;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    T0 = $[6];
    t1 = $[7];
    t2 = $[8];
    t3 = $[9];
    t4 = $[10];
    t5 = $[11];
    t6 = $[12];
  }
  let t7;
  if ($[23] !== T0 || $[24] !== t1 || $[25] !== t2) {
    t7 = <T0 className={t1}>{t2}</T0>;
    $[23] = T0;
    $[24] = t1;
    $[25] = t2;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  let t8;
  if ($[27] !== isPartner || $[28] !== shell) {
    t8 = isPartner ? <Container><MinimalFooter isPartner={true} isFullWidth={false} /></Container> : <SiteShellFooter shell={shell} />;
    $[27] = isPartner;
    $[28] = shell;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  let t9;
  if ($[30] !== t3 || $[31] !== t4 || $[32] !== t5 || $[33] !== t6 || $[34] !== t7 || $[35] !== t8) {
    t9 = <>{t3}{t4}{t5}{t6}{t7}{t8}</>;
    $[30] = t3;
    $[31] = t4;
    $[32] = t5;
    $[33] = t6;
    $[34] = t7;
    $[35] = t8;
    $[36] = t9;
  } else {
    t9 = $[36];
  }
  return t9;
}
function _temp(prev, section) {
  if (section?.__typename === "ComponentSectionsHeroV3" || section?.__typename === "ComponentSectionsHeroThree") {
    prev[0] = section;
  } else {
    if (section) {
      prev[1].push(section);
    }
  }
  return prev;
}
export default CMSPage;
type Params = {
  previewData?: {
    version: string;
  };
  params: {
    slug: string;
  };
  locale: string;
  preview?: boolean;
};
export type StaticPathsProps = {
  locales: string[];
  defaultLocale: string;
};
async function getLocalizedStaticPaths(context: StaticPathsProps) {
  const pages = await Promise.all(context.locales.map(async (locale: string) => {
    const localePages = await getPages({
      isCobranded: false,
      locale
    });
    if (!localePages?.pages?.data) return [];
    const strapiPages = (localePages.pages?.data as PageEntity[]).map(data => ({
      // Normalised here rather than at the split below, so the ignore list compares
      // against the same shape an editor sees in the address bar.
      slug: toSlugSegments(data.attributes?.slug).join("/"),
      locale
    }));
    return filterStrapiPages(strapiPages);
  }));
  return {
    paths: toStaticPagePaths(pages.flat()),
    fallback: config.cms.fallback
  };
}
const findSection = <T extends PageContentSectionsDynamicZone,>(page: Page, typename: string): T | undefined => {
  return page?.contentSections?.find(section => section?.__typename === typename) as T | undefined;
};
async function getLocalizedStaticProps({
  params,
  locale,
  preview
}: Params) {
  const slug = Array.isArray(params.slug) ? params.slug.join("/") : params.slug;
  const pageData = await getPageData({
    slug,
    locale,
    publicationState: preview ? "PREVIEW" : "LIVE"
  });
  if (!pageData?.attributes) {
    return {
      notFound: true
    };
  }
  const page = pageData.attributes;
  const shell = await getShellProps(locale);
  const sectionData: any = {};
  const customerReviewsSection = findSection<ComponentSectionsCustomerReviews>(page, "ComponentSectionsCustomerReviews");
  if (customerReviewsSection) {
    sectionData.ComponentSectionsCustomerReviews = await getStaticData();
  }
  Object.assign(sectionData, await getStaticSectionData(page.contentSections));
  return {
    props: {
      locale,
      ...(await serverSideTranslations(locale)),
      shell,
      page,
      sectionData,
      preview: !!preview
    },
    revalidate: config.cms.revalidate
  };
}
export const getStaticProps = withLocale(getLocalizedStaticProps);
export const getStaticPaths = withLocalePaths(getLocalizedStaticPaths);